import { useState, useRef } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { UploadCloud, FileUp, CheckCircle2, AlertTriangle, Copy, Loader2 } from "lucide-react";

export default function FoodCostImport() {
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);

  const onPick = (list) => setFiles(Array.from(list || []));
  const onDrop = (e) => { e.preventDefault(); onPick(e.dataTransfer.files); };

  const start = async () => {
    if (!files.length) return;
    setRunning(true); setDone(null); setJob(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data: created } = await api.post("/fc/import/jobs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob(created);
      let cur = created;
      while (cur && ["queued", "processing"].includes(cur.status)) {
        const { data } = await api.post(`/fc/import/jobs/${created.id}/process?limit=50`);
        cur = data; setJob(data);
      }
      setDone(cur);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore import");
    } finally { setRunning(false); }
  };

  const reset = () => { setFiles([]); setJob(null); setDone(null); if (inputRef.current) inputRef.current.value = ""; };
  const pct = job && job.total_files ? Math.round((job.processed_files / job.total_files) * 100) : 0;

  return (
    <div data-testid="fc-import-page">
      <PageHeader title="Food Cost · Importa fatture" subtitle="Carica gli XML FatturaPA (singoli, multipli o ZIP). Parsing 100% locale." testid="fc-import-header" />

      {!running && !done && (
        <Card className="p-8 rounded-xl" data-testid="fc-dropzone" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
          <div className="flex flex-col items-center text-center gap-3 py-6 border-2 border-dashed border-border rounded-xl">
            <UploadCloud className="w-10 h-10 text-[#C9A24B]" />
            <p className="font-dash text-lg">Trascina qui XML / ZIP / P7M</p>
            <p className="text-sm text-muted-foreground">oppure</p>
            <Button className="rounded-full" onClick={() => inputRef.current?.click()} data-testid="fc-select-files"><FileUp className="w-4 h-4 mr-1" />Seleziona file</Button>
            <input ref={inputRef} type="file" multiple accept=".xml,.zip,.p7m" className="hidden" onChange={(e) => onPick(e.target.files)} data-testid="fc-file-input" />
          </div>
          {files.length > 0 && (
            <div className="mt-5">
              <p className="text-sm" data-testid="fc-file-count">File selezionati: <b>{files.length}</b></p>
              <div className="mt-4 flex gap-2">
                <Button className="rounded-full" onClick={start} data-testid="fc-start-import"><UploadCloud className="w-4 h-4 mr-1" />Avvia importazione</Button>
                <Button variant="outline" className="rounded-full" onClick={reset} data-testid="fc-clear">Annulla</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {running && job && (
        <Card className="p-6 rounded-xl" data-testid="fc-progress">
          <div className="flex items-center gap-2 mb-3"><Loader2 className="w-5 h-5 animate-spin text-[#C9A24B]" /><h3 className="font-dash text-lg">Importazione fatture</h3></div>
          <p className="text-sm mb-1">{job.processed_files} / {job.total_files}</p>
          <Progress value={pct} className="h-3" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 text-sm">
            <Stat label="Importate" value={job.imported_files} />
            <Stat label="Duplicate" value={job.duplicate_files} />
            <Stat label="Conflitti" value={job.conflict_files} />
            <Stat label="Errori" value={job.error_files} />
            <Stat label="Prodotti associati" value={job.matched_lines} />
            <Stat label="Da associare" value={job.pending_lines} />
          </div>
          {job.last_processed_file && <p className="text-xs text-muted-foreground mt-3">Elaboro: {job.last_processed_file}</p>}
        </Card>
      )}

      {done && (
        <Card className="p-6 rounded-xl" data-testid="fc-report">
          <div className="flex items-center gap-2 mb-4 text-[hsl(var(--success))]"><CheckCircle2 className="w-6 h-6" /><h3 className="font-dash text-xl">Import completato</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Fatture elaborate" value={done.processed_files} />
            <Stat label="Importate" value={done.imported_files} />
            <Stat label="Duplicate" value={done.duplicate_files} />
            <Stat label="Conflitti" value={done.conflict_files} />
            <Stat label="Errori" value={done.error_files} />
            <Stat label="Righe prodotti" value={done.total_lines} />
            <Stat label="Riconosciuti" value={done.matched_lines} />
            <Stat label="Da associare" value={done.pending_lines} />
            <Stat label="Note di credito" value={done.credit_notes} />
          </div>
          <div className="mt-4 text-sm rounded-lg bg-secondary/60 p-3">
            <p>Acquisti TD01: <b>€ {(done.td01_total || 0).toFixed(2)}</b></p>
            <p>Note credito TD04: <b>−€ {(done.td04_total || 0).toFixed(2)}</b></p>
            <p className="mt-1">NETTO: <b>€ {((done.td01_total || 0) - (done.td04_total || 0)).toFixed(2)}</b></p>
          </div>
          {done.errors?.length > 0 && (
            <div className="mt-3" data-testid="fc-errors">
              <p className="text-sm font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-amber-600" />Errori ({done.errors.length})</p>
              <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">{done.errors.slice(0, 8).map((e, k) => <li key={k}>{e.filename}: {e.message}</li>)}</ul>
            </div>
          )}
          {done.conflicts?.length > 0 && (
            <div className="mt-3" data-testid="fc-conflicts">
              <p className="text-sm font-semibold flex items-center gap-1"><Copy className="w-4 h-4 text-amber-600" />Conflitti ({done.conflicts.length}) — da verificare in "Documenti da verificare"</p>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button className="rounded-full" onClick={reset} data-testid="fc-new-import"><UploadCloud className="w-4 h-4 mr-1" />Nuova importazione</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border/70 p-3" data-testid={`fc-stat-${label}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value ?? 0}</p>
    </div>
  );
}
